import React, { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Card, Col, Row, Descriptions, List, PageHeader, Tag, Table, Button, Modal, message as antdMessage } from "antd";

// Icons
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  NumberOutlined,
  SignalFilled,
  StopOutlined,
} from "@ant-design/icons";

import { useHistory, useParams } from "react-router-dom";
import moment from "moment";

// Firebase
import firebase from "firebase/compat/app";
import getFileIcon from "../../modules/helpers/fileIcon";
import { isServiceAccountRef, senderLabel } from "../../modules/helpers/sender";

import RecipientsTable from "./RecipientsTable";
import {
  AiFillClockCircle,
  AiFillDatabase,
  BiBuildings,
  BsFillPersonFill,
  BsPeopleFill,
  IoIosColorPalette,
  IoMdMail,
  MdLocalPostOffice,
  RiFileCopyFill,
} from "react-icons/all";

function TagStatus({ status }) {
  switch (status) {
    case "scheduled":
      return (
        <Tag icon={<ClockCircleOutlined />} color="processing">
          Programmé
        </Tag>
      );
    case "prepare":
      return (
        <Tag icon={<ClockCircleOutlined />} color="processing">
          En préparation
        </Tag>
      );
    case "cancelled":
      return (
        <Tag icon={<CloseCircleOutlined />} color="error">
          Annulé
        </Tag>
      );
    default:
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          Envoyé
        </Tag>
      );
  }
}

const customLabelStyle = { fontWeight: "bold", opacity: 0.75 };

const LabelContainer = ({ ...props }) => {
  return <div style={{ display: "flex", alignItems: "center", gap: "4px" }} {...props} />;
};

export default function Attachments() {
  const history = useHistory();

  const [message, setMessage] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [cancelStep, setCancelStep] = useState("none"); // none | info | confirm
  const [cancelling, setCancelling] = useState(false);
  const user = useSelector((state) => state.user, shallowEqual);

  const { type, id } = useParams();

  // Configure Firestore
  const db = firebase.firestore();

  const columnsHead = [
    { title: "Nom", dataIndex: "last_name" },
    { title: "Prénom", dataIndex: "first_name" },
    {
      title: "Type",
      dataIndex: "type",
      render: (type) => (type === "individual" ? "Particulier" : "Professionnel"),
    },
  ];

  const columnsTail = [
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => {
        switch (status) {
          case "prepare":
            return (
              <Tag icon={<ClockCircleOutlined />} color="processing">
                {message.date_type === "now" ? "En cours d'envoi" : "En préparation"}
              </Tag>
            );
          case "scheduled":
            return (
              <Tag icon={<ClockCircleOutlined />} color="processing">
                Programmé
              </Tag>
            );
          case "cancelled":
            return (
              <Tag icon={<CloseCircleOutlined />} color="error">
                Annulé
              </Tag>
            );
          default:
            return (
              <Tag icon={<CheckCircleOutlined />} color="success">
                Envoyé
              </Tag>
            );
        }
      },
    },
    {
      title: "Date de status",
      dataIndex: "last_updated",
      render: (lastUpdated) => moment(lastUpdated.toDate()).format("DD/MM/YYYY HH:mm"),
    },
  ];

  useEffect(() => {
    const messageRef = user.company.collection("messages").doc(id);

    messageRef.get().then((snapshot) => {
      const data = snapshot.data();

      if (data) {
        setMessage(data);

        messageRef
          .collection("recipients")
          .get()
          .then((recipientsSnapshot) => {
            setRecipients(recipientsSnapshot.docs.map((s) => s.data()));

            data.user.get().then((userSentSnapshot) => {
              user.company.get().then((companySnap) => {
                setMessage({
                  ...data,
                  sentBy: userSentSnapshot.data(),
                  userCompany: companySnap.data(),
                });
              });
            });
          });
      } else {
        history.replace("/historique");
      }
    });
  }, []);

  const trackingLink = useMemo(() => {
    if (message && message.trackingNumber && message.trackingType) {
      switch (message.trackingType) {
        case "laposte":
          return `https://www.laposte.fr/outils/suivre-vos-envois?code=${message.trackingNumber}`;
        default:
          return "";
      }
    }
    return "";
  }, [message]);

  // Annulation possible uniquement : papier, pas de statut opérateur posé
  // (traitement/erreur), et aucun destinataire pris en charge (spec 13_ANNULATION_ENVOI).
  const canCancel = useMemo(() => {
    if (!message || recipients.length === 0) return false;
    // Seul l'expéditeur du courrier peut annuler (décision 17/08/2026) ; pour un envoi d'un
    // compte de service (API), les administrateurs de la société le peuvent (décision 02/10/2026)
    if (isServiceAccountRef(message.user)) {
      if (!user.is_company_admin) return false;
    } else if (message.user?.id !== user.uid) {
      return false;
    }
    if (message.type !== "simple" && message.type !== "recommande") return false;
    if (message.status === "cancelled") return false;
    if (message.status && message.status !== "prepare") return false;
    return recipients.every((r) => r.status === "prepare" || r.status === "scheduled");
  }, [message, recipients, user.uid, user.is_company_admin]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const cancelMessage = firebase
        .app()
        .functions("europe-west1")
        .httpsCallable("cancelMessage");
      await cancelMessage({ companyId: user.company.id, messageId: id });
      antdMessage.success("Votre envoi a bien été annulé");
      window.location.reload();
    } catch (error) {
      const code = error?.code || "";
      const msg = error?.message || "";

      // Double clic / annulation déjà faite : rien à signaler, on rafraîchit
      if (msg.includes("déjà annulé")) {
        window.location.reload();
        return;
      }

      // Course avec l'opérateur : l'envoi vient d'être pris en charge.
      // Message dédié demandant de contacter le support.
      if (code.includes("failed-precondition")) {
        // Fermer la modale de confirmation sous-jacente avant d'afficher l'erreur
        setCancelStep("none");
        Modal.error({
          title: "Annulation impossible",
          content:
            "Cet envoi vient d'être pris en charge par nos équipes et ne peut plus être annulé. " +
            "Pour toute demande, veuillez contacter le support.",
          onOk: () => window.location.reload(),
        });
        setCancelling(false);
        return;
      }

      antdMessage.error(msg || "Une erreur est survenue lors de l'annulation. Veuillez contacter le support.");
      setCancelStep("none");
      setCancelling(false);
    }
  };

  return message === null ? (
    <div>Loading</div>
  ) : (
    <>
      <PageHeader
        onBack={() => {
          switch (message.type) {
            case "simple":
              history.push("/historique/lettres-simples");
              break;
            case "recommande":
              history.push("/historique/lettres-recommandees");
              break;
            case "electronique":
              history.push("/historique/electronique");
              break;
          }
        }}
        style={{ padding: 0, marginBottom: 24 }}
        title={
          <span>
            {"Détails de l'envoi N°" + id.toUpperCase()}{" "}
            <TagStatus status={message.status || (recipients[0] && recipients[0].status)} />
          </span>
        }
        extra={
          canCancel && (
            <Button danger icon={<StopOutlined />} onClick={() => setCancelStep("info")}>
              Annuler l'envoi
            </Button>
          )
        }
      />

      <Descriptions
        bordered
        title="Informations sur l'envoi"
        size="small"
        column={1}
        labelStyle={customLabelStyle}
      >
        <Descriptions.Item
          label={
            <LabelContainer>
              <NumberOutlined />
              N° d'envoi
            </LabelContainer>
          }
        >
          {id.toUpperCase()}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <LabelContainer>
              <BsFillPersonFill />
              Déclenché par
            </LabelContainer>
          }
        >
          {senderLabel(message.user, message.sentBy)}
        </Descriptions.Item>
        {message.userCompany && (
          <Descriptions.Item
            label={
              <LabelContainer>
                <BiBuildings />
                Entité émettrice
              </LabelContainer>
            }
          >
            {message.userCompany.name}
            <br />
            {message.userCompany.address_line_1}
            <br />
            {message.userCompany.address_line_2 && (
              <>
                {message.userCompany.address_line_2}
                <br />
              </>
            )}
            {message.userCompany.postal_code} {message.userCompany.city}
            <br />
          </Descriptions.Item>
        )}
        {message.type === "recommande" && (
          <Descriptions.Item
            label={
              <LabelContainer>
                <MdLocalPostOffice />
                N° de suivi
              </LabelContainer>
            }
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                {trackingLink && (
                  <span>
                    <span style={{ marginRight: ".5rem" }}>{message.trackingNumber}</span>
                    <a href={trackingLink} target="_blank">
                      Lien de suivi
                    </a>
                  </span>
                )}
              </div>
              {message.trackingFile && (
                <a target="_blank" href={message.trackingFile} style={{ marginRight: "1rem" }}>
                  Preuve de dépôt
                </a>
              )}
            </div>
          </Descriptions.Item>
        )}
      </Descriptions>

      {(message.type === "simple" || message.type === "recommande") && (
        <Descriptions
          bordered
          title="Options d'impression"
          size="small"
          style={{ marginTop: 32 }}
          column={6}
          labelStyle={customLabelStyle}
        >
          <Descriptions.Item
            label={
              <LabelContainer>
                <IoIosColorPalette />
                Paramètre couleur
              </LabelContainer>
            }
            span={3}
          >
            {message.color_option === "black_white" ? "Noir/Blanc" : "Couleur"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <LabelContainer>
                <RiFileCopyFill />
                Recto ou Recto/Verso
              </LabelContainer>
            }
            span={3}
          >
            {message.face_option === "one_face" ? "Recto seulement" : "Recto/Verso"}
          </Descriptions.Item>
          {message.type === "simple" && (
            <Descriptions.Item
              label={
                <LabelContainer>
                  <BsPeopleFill />
                  Type de destinataires
                </LabelContainer>
              }
              span={3}
            >
              {message.recipients_type === "unique"
                ? "Destinataire unique"
                : "Destinataires multiples"}
            </Descriptions.Item>
          )}
          {message.type === "simple" ? (
            <Descriptions.Item
              label={
                <LabelContainer>
                  <AiFillClockCircle />
                  Délai de distribution
                </LabelContainer>
              }
              span={3}
            >
              {message.envelope}
            </Descriptions.Item>
          ) : (
            <>
              <Descriptions.Item
                label={
                  <LabelContainer>
                    <IoMdMail />
                    Accusé de réception
                  </LabelContainer>
                }
                span={3}
              >
                {message.receipt_aknowledge === "yes"
                  ? "Avec accusé de réception"
                  : "Sans accusé de réception"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <LabelContainer>
                    <SignalFilled />
                    Niveau de recommandation
                  </LabelContainer>
                }
                span={6}
              >
                {message.level}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      )}

      {message.type === "electronique" && (
        <>
          <Descriptions
            bordered
            title="Options d'envoi"
            size="small"
            style={{ marginTop: 32 }}
            column={4}
            labelStyle={customLabelStyle}
          >
            <Descriptions.Item label="Type d'envoi" span={message.date_type === "now" ? 4 : 2}>
              {message.date_type === "now" ? "Immédiat" : "Différé"}
            </Descriptions.Item>
            {message.date_type !== "now" && message.date && (
              <Descriptions.Item label="Date d'envoi" span={2}>
                <span style={{ textTransform: "capitalize" }}>
                  {moment(message.date.toDate()).format("dddd D MMMM YYYY")}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Référence 1" span={2}>
              {message.ref1 || "Aucune"}
            </Descriptions.Item>
            <Descriptions.Item label="Référence 2" span={2}>
              {message.ref2 || "Aucune"}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions style={{ marginTop: 32 }} title="Destinataires" />
          <RecipientsTable data={recipients} />
        </>
      )}

      {message.type === "recommande" && recipients.length > 0 && recipients[0].last_name && (
        <>
          <Descriptions style={{ marginTop: 32 }} title="Destinataires" />
          <RecipientsTable data={recipients} messageType="recommande" />
        </>
      )}

      <Descriptions
        labelStyle={customLabelStyle}
        style={{ marginTop: 32 }}
        title={message.type === "electronique" ? "Pièces jointes" : "Documents"}
      />
      <Card type="inner" style={{ width: "100%" }} bodyStyle={{ padding: "8px 24px 0" }}>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <List
              itemLayout="horizontal"
              dataSource={
                message.attachments.length
                  ? message.attachments
                  : [
                      {
                        filename:
                          message.type === "electronique"
                            ? "Aucune pièce jointe"
                            : "Aucun document",
                      },
                    ]
              }
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={message.attachments.length ? getFileIcon(item.mimetype) : null}
                    title={item.filename}
                    description={
                      message.attachments.length ? (
                        <a href={item.url} rel="noopener noreferrer" target="_blank">
                          Ouvrir le fichier
                        </a>
                      ) : null
                    }
                  />
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        visible={cancelStep === "info"}
        title="Annuler l'envoi"
        onCancel={() => setCancelStep("none")}
        footer={[
          <Button key="back" onClick={() => setCancelStep("none")}>
            Retour
          </Button>,
          <Button key="continue" type="primary" onClick={() => setCancelStep("confirm")}>
            Continuer
          </Button>,
        ]}
      >
        <p>
          L'annulation est définitive et n'est possible que tant que votre demande n'a
          pas été prise en charge par nos équipes. Si votre envoi est déjà en cours de
          traitement, il ne pourra plus être annulé.
        </p>
      </Modal>

      <Modal
        visible={cancelStep === "confirm"}
        title="Confirmer l'annulation"
        onCancel={() => setCancelStep("none")}
        footer={[
          <Button key="back" onClick={() => setCancelStep("none")}>
            Retour
          </Button>,
          <Button
            key="confirm"
            danger
            type="primary"
            loading={cancelling}
            onClick={handleCancel}
          >
            Confirmer définitivement l'annulation
          </Button>,
        ]}
      >
        <p>
          Vous êtes sur le point d'annuler l'envoi N°{id.toUpperCase()} (
          {message.type === "simple" ? "lettre simple" : "recommandé papier"}) comprenant{" "}
          {recipients.length} destinataire{recipients.length > 1 ? "s" : ""}.
        </p>
        <p>Cette action est irréversible.</p>
      </Modal>

      {message.type === "electronique" && (
        <Descriptions
          bordered
          title="Contenu du message"
          size="small"
          column={4}
          style={{ marginTop: 32 }}
          labelStyle={customLabelStyle}
        >
          <Descriptions.Item label="Objet" span={4}>
            {message.subject}
          </Descriptions.Item>
          <Descriptions.Item label="Message" span={4}>
            {message.content}
          </Descriptions.Item>
        </Descriptions>
      )}
    </>
  );
}
